import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { GetPrivateRegionLeasedTimeMetricsRes } from '@/api/type-response'
import { message } from '@/components/base/Notification'
import { PrivateRegionNodeType } from '@/consts/privateRegion'

type UsageTimeRangeType = 'this_month' | 'last_3_months' | 'last_6_months' | 'last_12_months'

export function useChartUsageMetrics(
  selectedRegion: number | null,
  usageTimeRange: UsageTimeRangeType,
  selectedModel?: string | number,
) {
  const { t } = useTranslation('privateRegion')
  const [chartUsageMetrics, setChartUsageMetrics] =
    useState<GetPrivateRegionLeasedTimeMetricsRes | null>(null)
  const [loading, setLoading] = useState(false)

  const getUsageTimeRange = useCallback(() => {
    const now = dayjs()
    let startTime: dayjs.Dayjs

    switch (usageTimeRange) {
      case 'this_month':
        startTime = now.startOf('month')
        break
      case 'last_3_months':
        startTime = now.subtract(2, 'month').startOf('month')
        break
      case 'last_6_months':
        startTime = now.subtract(5, 'month').startOf('month')
        break
      case 'last_12_months':
        startTime = now.subtract(11, 'month').startOf('month')
        break
    }

    return { startTime, endTime: now.endOf('month') }
  }, [usageTimeRange])

  const loadData = useCallback(
    async (signal: AbortSignal) => {
      if (selectedRegion === null) return

      setLoading(true)
      try {
        const { startTime, endTime } = getUsageTimeRange()
        const metrics = await API.getPrivateRegionLeasedTimeMetrics(
          {
            region: selectedRegion,
            type: PrivateRegionNodeType.AllNode,
            start_time: startTime.format(),
            end_time: endTime.format(),
            model: selectedModel ? String(selectedModel) : undefined,
          },
          { signal },
        )

        // Set empty metrics array if API fails, so chart can still render (without lines)
        setChartUsageMetrics(metrics.code === 0 ? metrics : { metrics: [] })
      } catch (error: any) {
        // Don't show error if request was aborted
        if (error.name === 'AbortError' || error.name === 'CanceledError') return
        message.error(t('errors.failedToLoadUsageChartData'))
      } finally {
        setLoading(false)
      }
    },
    [selectedRegion, getUsageTimeRange, selectedModel, t],
  )

  useEffect(() => {
    if (selectedRegion === null) return

    const abortController = new AbortController()
    loadData(abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [selectedRegion, usageTimeRange, loadData])

  const refetch = useCallback(() => {
    if (selectedRegion !== null) {
      const abortController = new AbortController()
      loadData(abortController.signal)
    }
  }, [selectedRegion, loadData])

  return {
    chartUsageMetrics,
    loading,
    refetch,
  }
}
